import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { useAppSelector } from 'hooks/reduxHooks'
import useAccommodationMapSearchPath from 'hooks/Search/useAccommodationMapSearchPath'
import useMapSearchOfferUrl from 'hooks/Search/useMapSearchOfferUrl'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { getShouldUseSearchPrices } from 'lib/search/searchUtils'
import { useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import MetaSearchTile from '../MetaSearchTile'
import SearchTileRowOverlineLocation from '../shared/SearchTileRowOverlineLocation'
import BedbankSearchTileFeatures from './BedbankSearchTileFeatures'
import BedbankMetaSearchTilePriceStack from './BedbankMetaSearchTilePriceStack'
import BedbankSearchTileUnavailabilityBanner from './BedbankSearchTileUnavailabilityBanner'
import { generalLog } from 'marketing/api'
import BedbankMetaSearchTileAction from './BedbankMetaSearchTileAction'
import { selectLoggedIn } from 'selectors/accountSelectors'
import Label from 'LuxKit/Label'
import BedbankCancellationPolicy from 'components/OfferPage/Bedbank/BedbankCancellationPolicy'
import { buildBedbankCancellationPolicy } from 'lib/search/hotels'
import useProminentMetaSearchRate from 'hooks/useProminentMetaSearchRate'
import { useMetaSearchMobileV2Enabled } from 'components/Pages/HotelSearchPage/useMetaSearchMobileV2'
import MetaSearchV2AsSeenOnGoogleLabel from 'components/Pages/HotelSearchPage/MetaSearchV2/MetaSearchV2AsSeenOnGoogleLabel'
import type { SearchTileDensity, SearchTileTreatment } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { getAccommodationSearchTileView } from './accommodationSearchTileView'
import { isDisplayablePrice } from 'lib/offer/pricingVisibility'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  eagerLoadFirstImage?: boolean
  /**
   * @default default
   */
  density?: SearchTileDensity
  /**
   * @default default
   */
  treatment?: SearchTileTreatment
}

function BedbankMetaSearchTile({
  offer,
  filters,
  eagerLoadFirstImage,
  density = 'default',
  treatment = 'default',
}: Props) {
  const tileView = getAccommodationSearchTileView(density)
  const isMetaSearchMobileV2 = useMetaSearchMobileV2Enabled()
  const intl = useIntl()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const queryParams = useAppSelector((state) => state.router.location.query)
  const fullPath = useAppSelector((state) => state.router.location.pathname + state.router.location.search)
  const triggeredLog = useRef(false)
  const locations = useMemo<Array<string>>(() => {
    if (offer.property.locationTitleOverride) return [offer.property.locationTitleOverride]

    return [
      offer.property.address.city && offer.property.address.countryName
        ? intl.formatMessage(
            {
              id: 'Ar0b3rk4oW_3LYgoZ7XK',
              defaultMessage: '{city}, {country}',
              description: 'Bedbank meta search tile city and country separated by comma',
            },
            { city: offer.property.address.city, country: offer.property.address.countryName },
          )
        : (offer.property.address.city || offer.property.address.countryName || ''),
    ]
  }, [offer, intl])

  const mapSearchPath = useAccommodationMapSearchPath()
  const mapUrl = useMapSearchOfferUrl(false, offer, filters, mapSearchPath)

  const utmBp = useAppSelector(state => state.utm.bp)

  const shouldUseSearchPrices = getShouldUseSearchPrices(filters, true)

  const offerMetaData = useOfferMetaData(offer.id, filters)

  const shouldForceFetchRate = !!(filters?.checkIn && filters?.checkOut)
  const {
    offerPrice,
    shownRate,
    fetchingRates,
  } = useBedbankOfferSearchPricing(offer, filters, undefined, shouldForceFetchRate)
  const cancellationPolicy = useMemo(() => {
    return buildBedbankCancellationPolicy(
      offerMetaData?.cancellationPolicyInfo,
      filters,
      offer.property.timezone,
    )
  }, [offerMetaData?.cancellationPolicyInfo, filters, offer.property.timezone])

  const rateForPolicy = shownRate ?? cancellationPolicy

  const hasDates = !!(filters?.checkIn && filters.checkOut)
  const isSoldOut = shouldUseSearchPrices ? !offerMetaData?.available : (!shownRate && hasDates && !fetchingRates)

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  useEffect(() => {
    if (isSoldOut) {
      dispatchOfferListEvent({
        type: OfferListEvents.pricingCalculated,
        offerId: offer.id,
        availability: false,
        roomRateId: undefined,
        extendedAvailability: offerMetaData?.extendedAvailability,
        price: undefined,
        memberPrice: undefined,
        duration: undefined,
      })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isSoldOut])

  const hidePricing = !!offerMetaData?.hidePricing

  const {
    primaryPrice,
    primaryActionUtmBp,
    primaryFilters,
    primaryVariant,
    isProminent,
    b2cPrice,
    cugPrice,
  } = useProminentMetaSearchRate({
    isLoggedIn,
    ruleId: queryParams.ruleId,
    offer,
    filters,
    utmBp,
    priceTotal: Number(queryParams.priceTotal || 0),
    userCountry: queryParams.userCountry,
  })

  useEffect(() => {
    if (offerPrice && !triggeredLog.current) {
      const priceTotal = queryParams.priceTotal || 0
      generalLog({
        status: 'info',
        message: 'GoogleHotelSearchPage: highlightedPackage',
        payload: { ...queryParams, fullPath, priceReceived: offerPrice, priceDifference: Number(priceTotal) - offerPrice, isProminent, b2cPrice, cugPrice, isLoggedIn },
      })
      triggeredLog.current = true
    }
  }, [offerPrice, queryParams, fullPath, isProminent, primaryPrice, isLoggedIn, b2cPrice, cugPrice])

  const showPricingBlock = !isSoldOut && (hidePricing || isDisplayablePrice(primaryPrice.offerPrice))

  const showSearchedPropertyLabel = treatment !== 'featured-match'
  const searchedPropertyLabel = showSearchedPropertyLabel ? <Label kind="primary" variant="highlight">{intl.formatMessage({
    id: '_Vf2EUakXWbzPXJ2cG7z',
    defaultMessage: 'You searched for this property',
    description: 'Label on meta search tile indicating this is the property the user searched for',
  })}
  </Label> : undefined
  const productLabelElement = isMetaSearchMobileV2 ? <MetaSearchV2AsSeenOnGoogleLabel /> : searchedPropertyLabel

  return <MetaSearchTile
    state="default"
    density={density}
    treatment={isMetaSearchMobileV2 ? 'default' : treatment}
    eagerLoadFirstImage={eagerLoadFirstImage}
    actionPrimary={!isSoldOut && <BedbankMetaSearchTileAction offer={offer} filters={filters} utmBp={primaryActionUtmBp} />}
    actionSecondary={false}
    bookmarkAction={<BookmarkButton offer={offer} />}
    cancellationPolicyLabel={tileView.showCancellationPolicy && rateForPolicy && (
      <BedbankCancellationPolicy rate={rateForPolicy} showNonCancellation={false} />
    )}
    bottomExtension={isSoldOut &&
      <BedbankSearchTileUnavailabilityBanner
        offer={offer}
        unavailableReason={offerMetaData?.unavailableReason}
        filters={filters}
      />}
    features={<BedbankSearchTileFeatures
      offer={offer}
      filters={{ ...filters, isLuxPlusMember: false }}
    />}
    images={offer.images}
    overline={<SearchTileRowOverlineLocation locations={locations} mapUrl={mapUrl} showDistanceLabels={tileView.showLocationDistanceLabels} />}
    primaryPricePoint={showPricingBlock && <BedbankMetaSearchTilePriceStack
      offer={offer}
      filters={primaryFilters}
      hidePrice={hidePricing}
      hideIncludesTaxesAndFees={tileView.hideIncludesTaxesAndFees || isMetaSearchMobileV2}
    />}
    showPrimaryPrice
    hideSecondaryPrice
    productLabel={productLabelElement}
    title={offer.name}
    offer={offer}
    primaryVariant={primaryVariant}
  />
}

export default BedbankMetaSearchTile
